import PropTypes from "prop-types";
import { Fragment, memo, useCallback, useEffect, useState } from "react";
import Duration from "./Duration.jsx";
import findIndex from "lodash/findIndex";
import { offerTypePropTypes } from "app/utils/propTypes";
import { FormattedMessage } from "react-intl";
import "./BookingDurationList.scss";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { Button } from "app/components/ui/Button/Button";

const BookingDurationList = ({
	durations = [],
	durationValue, // used for when to collapse and the display of the duration neighbours of selected duration
	onDurationChange,
	amountSuffix,
	productInfo = {},
	offerType,
	isCollapsable,
	title,
}) => {
	const { track } = useAnalytics();

	const [isCollapsed, setCollapse] = useState(false);

	useEffect(() => {
		if (durationValue) {
			setCollapse(true);
		}
	}, [durationValue]);

	const showAllDurations = useCallback(() => {
		setCollapse(false);
		track("view_all_durations_clicked", { durationsCount: String(durations.length) });
	}, [isCollapsed, durations]);

	const hideAllDurations = useCallback(() => {
		setCollapse(true);
		track("hide_durations_clicked", { durationsCount: String(durations.length) });
	}, [isCollapsed, durations]);

	const handleDurationSelection = useCallback(value => {
		setCollapse(true);
		onDurationChange(value);
	}, []);

	let durationsNode = null;
	const durationsCount = durations.length;

	if (isCollapsed && isCollapsable && durationsCount > 0) {
		const selectedDurection = durationValue;
		const limitedDurations = [];

		const index = findIndex(durations, duration => {
			return duration.value === selectedDurection;
		});

		if (index === 0) {
			limitedDurations.push(durations[index]);
			if (durations[index + 1]) {
				limitedDurations.push(durations[index + 1]);
			}
			if (durations[index + 2]) {
				limitedDurations.push(durations[index + 2]);
			}
		} else if (index === durationsCount - 1) {
			if (durations[durationsCount - 3]) {
				limitedDurations.push(durations[durationsCount - 3]);
			}
			if (durations[durationsCount - 2]) {
				limitedDurations.push(durations[durationsCount - 2]);
			}
			limitedDurations.push(durations[durationsCount - 1]);
		} else {
			if (durations[index - 1]) {
				limitedDurations.push(durations[index - 1]);
			}
			if (durations[index]) {
				limitedDurations.push(durations[index]);
			}
			if (durations[index + 1]) {
				limitedDurations.push(durations[index + 1]);
			}
		}

		durationsNode = limitedDurations.map(duration => {
			return (
				<div key={duration.value} className="duration__item">
					<Duration
						offerType={offerType}
						productInfo={productInfo}
						amountSuffix={amountSuffix}
						duration={duration?.value}
						minPrice={duration?.minPrice?.p}
						publicPrice={duration?.minPrice?.pp}
						onChange={handleDurationSelection}
						data-testid={`search-engine-duration-${duration.value}`}
					/>
				</div>
			);
		});
	} else {
		durationsNode = durations.map(duration => {
			return (
				<div key={duration.value} className="duration__item">
					<Duration
						offerType={offerType}
						productInfo={productInfo}
						amountSuffix={amountSuffix}
						duration={duration?.value}
						minPrice={duration?.minPrice?.p}
						publicPrice={duration?.minPrice?.pp}
						onChange={handleDurationSelection}
						data-testid={`search-engine-duration-${duration.value}`}
					/>
				</div>
			);
		});
	}

	return (
		<Fragment>
			{title && !isCollapsed && isCollapsable && (
				<div className="duration__title">{title}</div>
			)}
			<div className="duration__list">{durationsNode}</div>
			{isCollapsable && (
				<Fragment>
					{isCollapsed && durations.length > 3 ? (
						<Button
							variant="borderless"
							className="duration__view-all"
							data-testid="view-all-durations-button"
							onClick={showAllDurations}
						>
							<FormattedMessage id="search.engine.duration.view.all.label" />
						</Button>
					) : (
						durationValue && (
							<Button
								variant="borderless"
								className="duration__view-all"
								data-testid="view-all-durations-button"
								onClick={hideAllDurations}
							>
								<FormattedMessage id="search.engine.duration.hide.label" />
							</Button>
						)
					)}
				</Fragment>
			)}
		</Fragment>
	);
};

BookingDurationList.propTypes = {
	offerType: offerTypePropTypes,
	durationValue: PropTypes.string,
	productInfo: PropTypes.object,
	durations: PropTypes.array,
	amountSuffix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	onDurationChange: PropTypes.func,
	isCollapsable: PropTypes.bool,
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default memo(BookingDurationList);
